<template>
  <v-container fluid>
    <v-select
      v-model="selectedWarehouse"
      :items="warehouses"
      item-text="name"
      item-value="warehouseId"
      return-object
      label="Warehouse"
    />
    <v-dialog
      v-model="createDialog"
      max-width="600px"
    >
      <v-card>
        <v-card-title>Create Internal Location</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newLocation.row"
            label="Row"
          />
          <v-text-field
            v-model="newLocation.rack"
            label="Rack"
          />
          <v-text-field
            v-model="newLocation.shelf"
            label="Shelf"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            @click="createDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!hasLocationPart"
            @click="createItem"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-btn
      color="primary"
      text
      :disabled="!selectedWarehouse"
      @click="createDialog = true"
    >
      New Internal Location
    </v-btn>
    <v-switch
      v-model="showInactive"
      label="Show inactive"
    />
    <v-dialog
      v-model="deactivateDialog"
      :persistent="deactivating"
      max-width="400px"
    >
      <v-card v-if="deactivateLocation">
        <v-toolbar
          dark
          color="error"
          dense
          flat
        >
          <v-spacer />
          <v-toolbar-title>Deactivate location</v-toolbar-title>
          <v-spacer />
        </v-toolbar>
        <v-card-text class="text-center pt-4">
          <template v-if="deactivateLocation.activeItemCount > 0">
            {{ deactivateLocation.activeItemCount }} item(s) are still in
            {{ deactivateLocation.name }}. Move them before deactivating.
          </template>
          <template v-else>
            Deactivate {{ deactivateLocation.name }}?
            It will no longer appear in location dropdowns. History is kept.
          </template>
          <v-alert
            v-if="deactivateError"
            type="error"
            class="mt-4"
          >
            {{ deactivateError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            v-if="deactivateLocation.activeItemCount > 0"
            color="primary"
            @click="viewItems"
          >
            View items
          </v-btn>
          <v-btn
            v-else
            color="error"
            :loading="deactivating"
            @click="confirmDeactivate"
          >
            Deactivate
          </v-btn>
          <v-btn
            text
            color="primary darken-1"
            :disabled="deactivating"
            @click="deactivateDialog = false"
          >
            {{ deactivateLocation.activeItemCount > 0 ? 'Close' : 'Cancel' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-simple-table>
      <thead>
        <tr>
          <th>Name</th>
          <th>Row</th>
          <th>Rack</th>
          <th>Shelf</th>
          <th>Items</th>
          <th>Active</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="location in items"
          :key="location.locationId"
        >
          <td>{{ location.name }}</td>
          <td>
            <v-text-field
              v-model="location.row"
              :disabled="location.system"
              hide-details
            />
          </td>
          <td>
            <v-text-field
              v-model="location.rack"
              :disabled="location.system"
              hide-details
            />
          </td>
          <td>
            <v-text-field
              v-model="location.shelf"
              :disabled="location.system"
              hide-details
            />
          </td>
          <td>{{ location.activeItemCount }}</td>
          <td>
            <v-switch
              v-model="location.active"
              :disabled="location.system"
              hide-details
              @change="activeChanged(location)"
            />
          </td>
          <td>
            <span v-if="location.system">System</span>
            <v-btn
              v-else
              color="primary"
              text
              @click="saveItem(location)"
            >
              Save
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'WarehouseLocationListComponent',
  data: () => ({
    warehouses: [],
    selectedWarehouse: null,
    items: [],
    newLocation: {
      row: null,
      rack: null,
      shelf: null,
    },
    createDialog: false,
    showInactive: false,
    deactivateDialog: false,
    deactivateLocation: null,
    deactivating: false,
    deactivateError: null,
  }),
  computed: {
    hasLocationPart() {
      return [this.newLocation.row, this.newLocation.rack, this.newLocation.shelf]
        .some((part) => part && part.trim());
    },
  },
  watch: {
    selectedWarehouse() {
      this.getLocations();
    },
    showInactive() {
      this.getLocations();
    },
  },
  created() {
    this.getWarehouses();
  },
  methods: {
    ...mapActions('apiClient', ['get', 'create', 'update']),
    getWarehouses() {
      this.get({ url: '/warehouses' }).then((response) => {
        this.warehouses = response.data;
        if (!this.selectedWarehouse && this.warehouses.length) {
          [this.selectedWarehouse] = this.warehouses;
        }
      });
    },
    createItem() {
      const body = {
        ...this.newLocation,
        warehouseId: this.selectedWarehouse.warehouseId,
      };
      this.create({ url: '/locations', body }).then(() => {
        this.createDialog = false;
        this.newLocation = { row: null, rack: null, shelf: null };
        this.getLocations();
      });
    },
    saveItem(location) {
      this.update({
        url: '/locations',
        id: location.locationId,
        body: location,
      }).then(() => this.getLocations());
    },
    async activeChanged(location) {
      if (!location.active) {
        await this.$nextTick();
        this.$set(location, 'active', true);
        this.deactivateLocation = location;
        this.deactivateError = null;
        this.deactivateDialog = true;
      }
    },
    async confirmDeactivate() {
      if (this.deactivating || this.deactivateLocation.activeItemCount > 0) return;
      this.deactivating = true;
      this.deactivateError = null;
      try {
        await this.update({
          url: '/locations',
          id: this.deactivateLocation.locationId,
          body: { ...this.deactivateLocation, active: false },
          handledStatuses: [409],
        });
        this.deactivateDialog = false;
        this.getLocations();
      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.$set(this.deactivateLocation, 'activeItemCount', error.response.data.activeItemCount);
        } else {
          this.deactivateError = 'Unable to deactivate this location. Please try again.';
        }
      } finally {
        this.deactivating = false;
      }
    },
    viewItems() {
      this.$router.push({
        path: '/inventory',
        query: {
          warehouseId: this.deactivateLocation.warehouseId,
          locationId: this.deactivateLocation.locationId,
        },
      });
    },
    getLocations() {
      if (!this.selectedWarehouse) {
        this.items = [];
        return;
      }
      this.get({
        url: '/locations',
        params: {
          warehouseId: this.selectedWarehouse.warehouseId,
          activeOnly: !this.showInactive,
        },
      }).then((response) => {
        this.items = response.data;
      });
    },
  },
};
</script>
